import { Requirement } from '../../../model/jira/release-item';
import { isRequirementsMappingFailed, RequirementTestcaseResult } from '../../../model/traceability/requirement-testcase';
import { TraceabilityDetail } from '../../../model/traceability/traceability-detail';

export const getTraceabilityDetails = (
  requirements: Requirement[],
  testCases: RequirementTestcaseResult[],
  jiraUiBaseUrl: string,
): TraceabilityDetail[] => {
  if (!requirements || !requirements.length || !testCases) {
    // this is a valid case, e.g. when no requirements are set for a pipeline run or the data are not yet loaded
    // console.error('deliveryRequirements or deliveryTestcases not set, cannot get meaningful requirement details');
    return [];
  }
  const traceabilityDetails = [];
  requirements.forEach((requirement) => {
    const testcases = getTestcases(requirement, testCases);
    const testStatus = getOverallTestStatus(testcases);
    traceabilityDetails.push({
      id: requirement.id.toString(),
      key: requirement.key,
      projectKey: requirement.key,
      testStatus,
      link: requirement.link ?? (jiraUiBaseUrl ? `${jiraUiBaseUrl}/browse/${requirement.key}` : null),
      testcases,
      status: {
        name: requirement.statusName,
        sort: getJiraStatusSort(requirement.statusName),
      },
      fixVersions: requirement.fixVersions,
      sort: getOverallTestStatusSort(testStatus),
      summary: requirement.summary,
    });
  });
  return traceabilityDetails;
};

const getOverallTestStatus = (tests: RequirementTestcaseResult[]): string => {
  if (!tests || !tests.length) {
    return 'No tests';
  }
  for (const test of tests.filter((ts) => !!ts)) {
    if (isRequirementsMappingFailed(test) || !test.status || !test.name.length || test.skipped || test.hasIncorrectVersion) {
      return 'Not OK';
    }
  }
  return 'OK';
};

const getTestcases = (jiraElement: any, testCases: RequirementTestcaseResult[]): RequirementTestcaseResult[] => {
  return testCases.filter((test) => test?.requirementKey.toUpperCase() === jiraElement.key.toUpperCase());
};

const getJiraStatusSort = (jiraStatus: string): number => {
  switch (jiraStatus) {
    case 'Reopened':
    case 'Blocked':
    case 'Open':
      return 1;
    case 'In Progress':
      return 2;
    case 'Consumed':
    case 'Completed':
    case 'Inactive':
      return 3;
    default:
      // NOTE: Jira statuses can be customized, so we cannot cover all cases here
      return 0;
  }
};

const getOverallTestStatusSort = (overallTestStatus: string): number => {
  switch (overallTestStatus) {
    case 'Not OK':
      return 1;
    case 'No tests':
      return 2;
    case 'OK':
      return 3;
    default:
      console.warn(`Unknown overall test status: ${overallTestStatus}`);
      return 0;
  }
};
